import {EDeviceOrientationEnum} from '../models/device-orientation.enum';
import {IDeviceOrientationAngle} from '../models';

/**
 * Returns the current screen orientation as a DeviceOrientationEnum.
 * Uses `window.screen.orientation.type` if available.
 *
 * @returns {EDeviceOrientationEnum} One of:
 *   - 'portrait-primary'
 *   - 'portrait-secondary'
 *   - 'landscape-primary'
 *   - 'landscape-secondary'
 *   - 'unknown' if the orientation cannot be determined
 */
export function getDeviceOrientation(): EDeviceOrientationEnum {
  const type = window.screen.orientation?.type;

  switch (type) {
    case 'portrait-primary':
      return EDeviceOrientationEnum.PortraitPrimary;
    case 'portrait-secondary':
      return EDeviceOrientationEnum.PortraitSecondary;
    case 'landscape-primary':
      return EDeviceOrientationEnum.LandscapePrimary;
    case 'landscape-secondary':
      return EDeviceOrientationEnum.LandscapeSecondary;
    default:
      return EDeviceOrientationEnum.Unknown;
  }
}

/**
 * Extracts the device orientation angles (in degrees) from a DeviceOrientationEvent.
 * These angles describe the device's rotation in 3D space:
 *   - alpha: rotation around the Z-axis (0–360°)
 *   - beta: front-to-back tilt (-180–180°)
 *   - gamma: left-to-right tilt (-90–90°)
 *
 * @param {Event} event The device orientation event
 * @returns {IDeviceOrientationAngle} Object containing alpha, beta, and gamma angles
 */
export function getDeviceOrientationAngle(event: Event): IDeviceOrientationAngle {
  const deviceEvent = event as DeviceOrientationEvent;

  return {
    alpha: deviceEvent?.alpha ?? 0,
    beta: deviceEvent?.beta ?? 0,
    gamma: deviceEvent?.gamma ?? 0,
  };
}

/**
 * Returns the current screen rotation angle in degrees.
 * Uses the Screen Orientation API if available.
 *
 * Possible values:
 * - 0    → Portrait-primary
 * - 90   → Landscape-primary
 * - 180  → Portrait-secondary
 * - 270  → Landscape-secondary
 *
 * @returns {number} Screen rotation angle in degrees (0, 90, 180, 270). Defaults to 0 if unavailable.
 */
export function getScreenOrientationAngle(): number {
  return window.screen.orientation?.angle ?? 0;
}


